import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AppLayout from '../components/AppLayout';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  getSuggestedReviewCycleName as getFiscalSuggestedName,
} from '../utils/fiscalYear';
import { fiscalYearService } from '../services/fiscalYearService';
import {
  getCalendarYear,
  getCalendarQuarter,
  getSuggestedReviewCycleName as getCalendarSuggestedName,
  getCurrentCalendarYear,
} from '../utils/calendarYear';
import { formatDate, formatDateRange, formatCalendarDateIso, parseUtcDate, utcMs } from '../utils/dateFormat';
import { createShouldDisableDate } from '../utils/dateValidation';
import {
  buildSuggestedReviewCycleDates,
  dovetailAdjustmentMessage,
} from '../utils/reviewCycleDates';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSubmit, couldNotUpdate, couldNotCreate } from '../utils/userFacingCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { closedSuccessfully } from '../utils/successCopy';

interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  employeeCutoff?: string;
  isActive: boolean;
  createdAt: string;
  reviewCount: number;
  outstandingTasksCount: number;
}

interface CreateReviewCycleForm {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  employeeCutoff: Date | null;
}

export default function ReviewCyclesPage() {
  const navigate = useNavigate();
  const showSuccess = useSuccessSnackbar();
  const { user, displayTimeZoneId } = useAuth();
  const isAdmin = user?.roles.includes('Administrator') ?? false;
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [useCalendarYear, setUseCalendarYear] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<ReviewCycle | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuCycleId, setMenuCycleId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<CreateReviewCycleForm>({
    name: '',
    startDate: null,
    endDate: null,
    employeeCutoff: null,
  });
  const [editForm, setEditForm] = useState<CreateReviewCycleForm>({
    name: '',
    startDate: null,
    endDate: null,
    employeeCutoff: null,
  });
  const [createError, setCreateError] = useState<string>('');
  const [createDovetailInfo, setCreateDovetailInfo] = useState<string>('');
  const [editError, setEditError] = useState<string>('');
  const [sortField, setSortField] = useState<keyof ReviewCycle | null>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [cycleToClose, setCycleToClose] = useState<string | null>(null);
  const [currentFiscalYear, setCurrentFiscalYear] = useState<number | null>(null);

  const loadReviewCycles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get<ReviewCycle[]>('/review-cycles');
      setReviewCycles(response.data);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('review cycles')));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReviewCycleSettings = useCallback(async () => {
    // Only load settings if user is admin (settings endpoint requires admin access)
    if (!isAdmin) {
      // Default to fiscal year for non-admins
      setUseCalendarYear(false);
      return;
    }

    try {
      const response = await apiService.get<{ useCalendarYear: boolean; autoCreateCycles: boolean }>('/System/review-cycle-settings');
      setUseCalendarYear(response.data.useCalendarYear);
    } catch {
      // Default to fiscal year if settings can't be loaded
      setUseCalendarYear(false);
    }
  }, [isAdmin]);

  const handleSort = (field: keyof ReviewCycle) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCycles = useMemo(() => {
    if (!sortField) return reviewCycles;

    return [...reviewCycles].sort((a, b) => {
      let aValue: string | number | boolean | null | undefined = a[sortField];
      let bValue: string | number | boolean | null | undefined = b[sortField];

      // Handle date strings - check for null/undefined before conversion
      const isDateField = sortField === 'startDate' || sortField === 'endDate' || sortField === 'employeeCutoff' || sortField === 'createdAt';

      if (isDateField) {
        // For date fields, handle null/undefined first
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1; // null dates go to the end
        if (bValue == null) return -1; // null dates go to the end

        // Convert to timestamps for comparison
        aValue = utcMs(aValue as string);
        bValue = utcMs(bValue as string);
      } else {
        // For non-date fields, handle null/undefined
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
      }

      // Handle boolean (isActive)
      if (sortField === 'isActive') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reviewCycles, sortField, sortDirection]);

  const getSortIcon = (field: keyof ReviewCycle) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  useEffect(() => {
    loadReviewCycleSettings();
    loadReviewCycles();
  }, [loadReviewCycles, loadReviewCycleSettings]);

  useEffect(() => {
    if (createDialogOpen && !useCalendarYear) {
      fiscalYearService.getCurrent().then((r) => setCurrentFiscalYear(r.fiscalYear)).catch(() => setCurrentFiscalYear(null));
    } else if (useCalendarYear) {
      setCurrentFiscalYear(null);
    }
  }, [createDialogOpen, useCalendarYear]);

  const handleCreateReviewCycle = async () => {
    if (!createForm.name || !createForm.startDate || !createForm.endDate) {
      setCreateError('Please fill in all required fields');
      return;
    }

    if (createForm.startDate >= createForm.endDate) {
      setCreateError('End date must be after start date');
      return;
    }

    if (!createForm.employeeCutoff) {
      setCreateError('Employee cutoff is required');
      return;
    }
    if (createForm.employeeCutoff > createForm.endDate) {
      setCreateError('Employee cutoff cannot be after end date');
      return;
    }

    try {
      setCreating(true);
      setCreateError('');
      await apiService.post('/review-cycles', {
        name: createForm.name,
        startDate: formatCalendarDateIso(createForm.startDate),
        endDate: formatCalendarDateIso(createForm.endDate),
        employeeCutoff: formatCalendarDateIso(createForm.employeeCutoff),
      });
      setCreateDialogOpen(false);
      setCreateForm({ name: '', startDate: null, endDate: null, employeeCutoff: null });
      setCreateDovetailInfo('');
      loadReviewCycles();
    } catch (err: unknown) {
            setCreateError(getApiErrorMessage(err, couldNotCreate('the review cycle')));
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (cycle: ReviewCycle) => {
    setSelectedCycle(cycle);
    setEditForm({
      name: cycle.name,
      startDate: parseUtcDate(cycle.startDate)!,
      endDate: parseUtcDate(cycle.endDate)!,
      employeeCutoff: parseUtcDate(cycle.employeeCutoff),
    });
    setEditDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleUpdateReviewCycle = async () => {
    if (!selectedCycle || !editForm.name || !editForm.startDate || !editForm.endDate || !editForm.employeeCutoff) {
      setEditError('Please fill in all required fields');
      return;
    }

    // If cycle is active, validate that we're only extending (not shortening)
    if (selectedCycle.isActive) {
      const originalEndDate = parseUtcDate(selectedCycle.endDate)!;
      if (editForm.endDate < originalEndDate) {
        setEditError('Cannot shorten the end date of an active review cycle. You can only extend it.');
        return;
      }

      // Start date should not change for active cycles (compare by calendar date to avoid timezone/normalization false positives)
      const originalStartDate = parseUtcDate(selectedCycle.startDate)!;
      if (formatCalendarDateIso(editForm.startDate) !== formatCalendarDateIso(originalStartDate)) {
        setEditError('Cannot change the start date of an active review cycle.');
        return;
      }
    } else {
      // For inactive cycles, normal validation
      if (editForm.startDate >= editForm.endDate) {
        setEditError('End date must be after start date');
        return;
      }
    }

    if (!editForm.employeeCutoff) {
      setEditError('Employee cutoff is required');
      return;
    }
    if (editForm.employeeCutoff > editForm.endDate) {
      setEditError('Employee cutoff cannot be after end date');
      return;
    }

    try {
      setUpdating(true);
      setEditError('');
      await apiService.put(`/review-cycles/${selectedCycle.id}`, {
        name: editForm.name,
        startDate: formatCalendarDateIso(editForm.startDate),
        endDate: formatCalendarDateIso(editForm.endDate),
        employeeCutoff: formatCalendarDateIso(editForm.employeeCutoff),
      });
      setEditDialogOpen(false);
      setSelectedCycle(null);
      loadReviewCycles();
    } catch (err: unknown) {
            setEditError(getApiErrorMessage(err, couldNotUpdate('the review cycle')));
    } finally {
      setUpdating(false);
    }
  };

  const handleStartCycle = async (cycleId: string) => {
    try {
      setActionLoading(cycleId);
      await apiService.put(`/review-cycles/${cycleId}/start`, {});
      loadReviewCycles();
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotSubmit('the review cycle start')));
    } finally {
      setActionLoading(null);
      setMenuAnchor(null);
    }
  };

  const handleCloseCycleClick = (cycleId: string) => {
    setCycleToClose(cycleId);
    setCloseConfirmOpen(true);
    setMenuAnchor(null);
  };

  const handleCloseCycleConfirm = async () => {
    if (!cycleToClose) return;

    try {
      setActionLoading(cycleToClose);
      await apiService.put(`/review-cycles/${cycleToClose}/close`, {});
      loadReviewCycles();
      showSuccess(closedSuccessfully('Review cycle'));
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotSubmit('the review cycle close')));
    } finally {
      setActionLoading(null);
      setCloseConfirmOpen(false);
      setCycleToClose(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cycleId: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuCycleId(cycleId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuCycleId(null);
  };

  const handleViewOutstanding = (cycleId: string) => {
    navigate(`/review-cycles/${cycleId}/outstanding`);
    setMenuAnchor(null);
    setMenuCycleId(null);
  };

  const handleStartDateChange = async (date: Date | null) => {
    setCreateDovetailInfo('');
    setCreateForm({ ...createForm, startDate: date });
    if (date) {
      let suggestedName: string;
      if (useCalendarYear) {
        const year = getCalendarYear(date);
        const quarter = getCalendarQuarter(date);
        suggestedName = getCalendarSuggestedName(year, quarter);
        if (!createForm.name) {
          setCreateForm((prev) => ({ ...prev, startDate: date, name: suggestedName }));
        }
      } else {
        try {
          const { fiscalYear, quarter } = await fiscalYearService.evaluateDate(date);
          suggestedName = getFiscalSuggestedName(fiscalYear, quarter);
          if (!createForm.name) {
            setCreateForm((prev) => ({ ...prev, startDate: date, name: suggestedName }));
          }
        } catch {
          // Leave name unchanged if API fails
        }
      }
    }
  };

  const handleSuggestQuarter = async (year: number, quarter: number) => {
    const applyRange = (name: string, quarterStart: Date, quarterEnd: Date) => {
      const suggested = buildSuggestedReviewCycleDates(
        quarterStart,
        quarterEnd,
        reviewCycles.map((c) => ({ name: c.name, startDate: c.startDate, endDate: c.endDate }))
      );
      if (!suggested.ok) {
        setCreateError(suggested.message);
        setCreateDovetailInfo('');
        return;
      }
      setCreateError('');
      setCreateDovetailInfo(dovetailAdjustmentMessage(suggested) ?? '');
      setCreateForm({
        name,
        startDate: suggested.startDate,
        endDate: suggested.endDate,
        employeeCutoff: suggested.employeeCutoff,
      });
    };

    if (useCalendarYear) {
      const startMonth = (quarter - 1) * 3;
      applyRange(
        getCalendarSuggestedName(year, quarter),
        new Date(Date.UTC(year, startMonth, 1)),
        new Date(Date.UTC(year, startMonth + 3, 0))
      );
    } else {
      try {
        const range = await fiscalYearService.getQuarterRange(year, quarter);
        applyRange(
          getFiscalSuggestedName(year, quarter),
          parseUtcDate(range.start)!,
          parseUtcDate(range.end)!
        );
      } catch {
        // Leave form unchanged if API fails
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ ml: -1 }}
                title="Back"
              >
                <ArrowBackIcon />
              </IconButton>
              <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
                {isAdmin ? 'Review Cycles Management' : 'Review Cycles'}
              </Typography>
            </Box>
            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/settings')}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Settings
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreateError('');
                    setCreateDovetailInfo('');
                    setCreateForm({ name: '', startDate: null, endDate: null, employeeCutoff: null });
                    setCreateDialogOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Create Review Cycle
                </Button>
              </Box>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            {isAdmin
              ? 'Manage performance review cycles, create new cycles, and monitor review progress across the organisation.'
              : 'View your assigned review cycles and track the status of your performance reviews.'}
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card
            sx={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <TableContainer>
                <Table key={displayTimeZoneId}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('name')}
                        >
                          <strong>Name</strong>
                          {getSortIcon('name')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('startDate')}
                        >
                          <strong>Period</strong>
                          {getSortIcon('startDate')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('employeeCutoff')}
                        >
                          <strong>Employee cutoff</strong>
                          {getSortIcon('employeeCutoff')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('isActive')}
                        >
                          <strong>Status</strong>
                          {getSortIcon('isActive')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('createdAt')}
                        >
                          <strong>Created</strong>
                          {getSortIcon('createdAt')}
                        </Box>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <strong>Outstanding Tasks</strong>
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCycles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 7 : 5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No review cycles found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedCycles.map((cycle) => (
                        <TableRow key={cycle.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {cycle.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDateRange(cycle.startDate, cycle.endDate)}
                          </TableCell>
                          <TableCell>
                            {cycle.employeeCutoff ? formatDate(cycle.employeeCutoff) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cycle.isActive ? 'Active' : 'Closed'}
                              color={cycle.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(cycle.createdAt, { includeTime: true })}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              {cycle.outstandingTasksCount > 0 ? (
                                <Chip
                                  label={cycle.outstandingTasksCount}
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                  onClick={() => handleViewOutstanding(cycle.id)}
                                  title="View outstanding tasks"
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  0
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell align="right">
                              <IconButton
                                onClick={(e) => handleMenuOpen(e, cycle.id)}
                                disabled={actionLoading === cycle.id}
                              >
                                {actionLoading === cycle.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <MoreVertIcon />
                                )}
                              </IconButton>
                              <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor) && menuCycleId === cycle.id}
                                onClose={handleMenuClose}
                              >
                                {/* Only show Edit for active cycles or cycles that were never started (no reviews) */}
                                {cycle.isActive || cycle.reviewCount === 0 ? (
                                  <MenuItem onClick={() => handleEditClick(cycle)}>
                                    <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                                    {cycle.isActive ? 'Extend/Update' : 'Edit'}
                                  </MenuItem>
                                ) : null}
                                {/* Only show Start for cycles that were never started (not active and no reviews) */}
                                {!cycle.isActive && cycle.reviewCount === 0 && (
                                  <MenuItem onClick={() => handleStartCycle(cycle.id)}>
                                    <PlayArrowIcon sx={{ mr: 1, fontSize: 20 }} />
                                    Start Cycle
                                  </MenuItem>
                                )}
                                {cycle.isActive && (
                                  <MenuItem onClick={() => handleViewOutstanding(cycle.id)}>
                                    <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
                                    View Outstanding Tasks
                                  </MenuItem>
                                )}
                                {cycle.isActive && (
                                  <MenuItem onClick={() => handleCloseCycleClick(cycle.id)}>
                                    <StopIcon sx={{ mr: 1, fontSize: 20 }} />
                                    Close Cycle
                                  </MenuItem>
                                )}
                              </Menu>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => !creating && setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography component="span" variant="h6" fontWeight="600">
              Create Review Cycle
            </Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} disabled={creating}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>
                {createError}
              </Alert>
            )}
            {createDovetailInfo && !createError && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCreateDovetailInfo('')}>
                {createDovetailInfo}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Quick suggestions:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1, 2, 3, 4].map((q) => {
                      if (useCalendarYear) {
                        const currentYear = getCurrentCalendarYear();
                        return (
                          <Button
                            key={q}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSuggestQuarter(currentYear, q)}
                            disabled={creating}
                            sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                          >
                            {currentYear} Q{q}
                          </Button>
                        );
                      } else {
                        const currentFY = currentFiscalYear ?? 0;
                        return (
                          <Button
                            key={q}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSuggestQuarter(currentFY, q)}
                            disabled={creating || currentFiscalYear === null}
                            sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                          >
                            FY {currentFY} Q{q}
                          </Button>
                        );
                      }
                    })}
                  </Box>
                </Box>
                <TextField
                  label="Cycle Name"
                  fullWidth
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  disabled={creating}
                  required
                  placeholder={useCalendarYear ? "e.g., 2024 Q3 Review" : "e.g., FY 2024 Q3 Review"}
                />
              </Box>
              <AppDatePicker
                label="Start Date"
                value={createForm.startDate}
                onChange={handleStartDateChange}
                disabled={creating}
                shouldDisableDate={shouldDisableDate}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <AppDatePicker
                label="End Date"
                value={createForm.endDate}
                onChange={(date) => {
                  setCreateDovetailInfo('');
                  setCreateForm({ ...createForm, endDate: date });
                }}
                disabled={creating}
                shouldDisableDate={shouldDisableDate}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <AppDatePicker
                label="Employee cutoff"
                value={createForm.employeeCutoff}
                onChange={(date) => {
                  setCreateDovetailInfo('');
                  setCreateForm({ ...createForm, employeeCutoff: date });
                }}
                disabled={creating}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: 'Date by which employees must complete their part (e.g. self-review) so HR and managers can complete theirs.',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReviewCycle}
              variant="contained"
              disabled={creating}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              {creating ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => !updating && setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography component="span" variant="h6" fontWeight="600">
              Edit Review Cycle
            </Typography>
            <IconButton onClick={() => setEditDialogOpen(false)} disabled={updating}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {selectedCycle?.isActive && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  This cycle is active. You can extend the end date and update the employee cutoff, but cannot change the start date or shorten the end date.
                </Alert>
              )}
              <TextField
                label="Cycle Name"
                fullWidth
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                disabled={updating}
                required
              />
              <AppDatePicker
                label="Start Date"
                value={editForm.startDate}
                onChange={(date) => setEditForm({ ...editForm, startDate: date })}
                disabled={updating || selectedCycle?.isActive}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: selectedCycle?.isActive ? 'Cannot change start date of active cycle' : undefined
                  }
                }}
              />
              <AppDatePicker
                label="End Date"
                value={editForm.endDate}
                onChange={(date) => {
                  if (date && selectedCycle?.isActive) {
                    const originalEndDate = parseUtcDate(selectedCycle.endDate)!;
                    // Only allow extending, not shortening
                    if (date < originalEndDate) {
                      setEditError('Cannot shorten the end date of an active cycle. You can only extend it.');
                      return;
                    }
                  }
                  setEditForm({ ...editForm, endDate: date });
                }}
                disabled={updating}
                shouldDisableDate={shouldDisableDate}
                minDate={selectedCycle?.isActive ? parseUtcDate(selectedCycle.endDate) ?? undefined : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: selectedCycle?.isActive
                      ? 'You can only extend the end date (not shorten it) for active cycles'
                      : undefined
                  }
                }}
              />
              <AppDatePicker
                label="Employee cutoff"
                value={editForm.employeeCutoff}
                onChange={(date) => setEditForm({ ...editForm, employeeCutoff: date })}
                disabled={updating}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: 'Date by which employees must complete their part (e.g. self-review) so HR and managers can complete theirs.',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)} disabled={updating} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateReviewCycle}
              variant="contained"
              disabled={updating}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              {updating ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Close Cycle Confirmation Dialog */}
        <Dialog
          open={closeConfirmOpen}
          onClose={() => setCloseConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography component="span" variant="h6" fontWeight="600">
              Close Review Cycle
            </Typography>
            <IconButton onClick={() => setCloseConfirmOpen(false)} disabled={actionLoading === cycleToClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {cycleToClose && (() => {
              const cycle = reviewCycles.find(c => c.id === cycleToClose);
              const hasOutstandingTasks = cycle && cycle.outstandingTasksCount > 0;
              return (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to close <strong>{cycle?.name || 'this review cycle'}</strong>? This action cannot be undone.
                  </Typography>
                  {hasOutstandingTasks && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Warning: This cycle has {cycle.outstandingTasksCount} outstanding task{cycle.outstandingTasksCount !== 1 ? 's' : ''}.
                      </Typography>
                      <Typography variant="body2">
                        Closing the cycle will prevent completion of pending self-reviews, manager reviews, or peer feedback.
                        Consider waiting until all tasks are completed or sending reminders first.
                      </Typography>
                    </Alert>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Closing a cycle will prevent any further reviews or updates from being made to it.
                  </Typography>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button
              onClick={() => setCloseConfirmOpen(false)}
              disabled={actionLoading === cycleToClose}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloseCycleConfirm}
              variant="contained"
              color="error"
              disabled={actionLoading === cycleToClose}
              startIcon={actionLoading === cycleToClose ? <CircularProgress size={20} /> : <StopIcon />}
              sx={{ textTransform: 'none' }}
            >
              {actionLoading === cycleToClose ? 'Closing...' : 'Close Cycle'}
            </Button>
          </DialogActions>
        </Dialog>
      </AppLayout>
    </LocalizationProvider>
  );
}

